import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash';
import { getSessionInfo, logoutSession } from './api';
import { parseQueryString } from '../uri';
import { performRedirectTo } from '../redirect-to';

const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [state, setState] = useState({
    sessionInfo: null,
    loading: false,
    error: null,
  });

  const fetchSession = useCallback((applicationId, to) => {
    setState((s) => ({ ...s, loading: true }));
    return getSessionInfo(applicationId, to)
      .then((sessionInfo) => {
        setState((s) => ({ ...s, sessionInfo, error: null, loading: false }));
        return sessionInfo;
      })
      .catch((error) => {
        setState((s) => ({ ...s, error, loading: false }));
        return Promise.reject(error);
      });
  }, []);

  const logout = useCallback(() => {
    return logoutSession().then(() => {
      setState((s) => ({ ...s, sessionInfo: null }));
    });
  }, []);

  useEffect(() => {
    const { applicationId, to } = parseQueryString(global.window.location.search);
    const fetchSessionWithRedirect = () => {
      fetchSession(applicationId, to)
        .then((session) => {
          if (session.state === 'ApplicationAccessGranted') {
            performRedirectTo(session.redirectUri);
          }
        })
        .catch(() => {});
    };

    fetchSessionWithRedirect();

    const handleFocus = throttle(fetchSessionWithRedirect, 5000, { trailing: false });

    global.window.addEventListener('focus', handleFocus);
    return () => {
      global.window.removeEventListener('focus', handleFocus);
      handleFocus.cancel();
    };
  }, [fetchSession]);

  return <SessionContext.Provider value={{ ...state, fetchSession, logout }}>{children}</SessionContext.Provider>;
};
